import {
    memo,
    useEffect,
    useLayoutEffect,
    useMemo,
} from 'react';
import {
    Color,
    ShaderMaterial,
} from 'three';
import type { Texture } from 'three';
import {
    SHADOW_ALPHA_TEST,
    SHADOW_COLOR_BLEND,
    SHADOW_OPACITY,
} from './config';
import type { ModelViewerPedestalLayout } from '../Presentation/Pedestal';
import type { ShadowModel } from './types';

interface Props {
    fadeEdges: boolean
    model: ShadowModel
    pedestal?: ModelViewerPedestalLayout
    texture: Texture
}

function createCachedShadowMaterial():ShaderMaterial {
    return new ShaderMaterial({
        depthWrite: false,
        fragmentShader: `
            uniform sampler2D shadowMap;
            uniform vec3 shadowColor;
            uniform float opacity;
            uniform float alphaTest;
            uniform float colorBlend;
            uniform float fadeEdges;
            varying vec2 vShadowUv;

            void main() {
                float whitePoint = max(
                    max(
                        texture2D(shadowMap, vec2(0.05, 0.05)).r,
                        texture2D(shadowMap, vec2(0.95, 0.05)).r
                    ),
                    max(
                        texture2D(shadowMap, vec2(0.05, 0.95)).r,
                        texture2D(shadowMap, vec2(0.95, 0.95)).r
                    )
                );
                float sampledShadow = texture2D(shadowMap, vShadowUv).r;
                float normalizedShadow = clamp(
                    sampledShadow / max(whitePoint, 0.0001),
                    0.0,
                    1.0
                );
                float shadow = mix(sampledShadow, normalizedShadow, fadeEdges);
                float distanceToCenter = max(
                    abs(vShadowUv.x - 0.5),
                    abs(vShadowUv.y - 0.5)
                ) * 2.0;
                float edgeMask = 1.0 - smoothstep(0.72, 1.0, distanceToCenter);
                gl_FragColor = vec4(
                    shadowColor * shadow * (alphaTest / 3.0) * colorBlend,
                    (1.0 - shadow) * opacity * mix(1.0, edgeMask, fadeEdges)
                );
                #include <tonemapping_fragment>
                #include <colorspace_fragment>
            }
        `,
        toneMapped: true,
        transparent: true,
        uniforms: {
            alphaTest: {value: SHADOW_ALPHA_TEST},
            colorBlend: {value: SHADOW_COLOR_BLEND},
            fadeEdges: {value: 0},
            opacity: {value: SHADOW_OPACITY},
            shadowColor: {value: new Color('black')},
            shadowMap: {value: null},
            shadowScale: {value: 1},
        },
        vertexShader: `
            uniform float shadowScale;
            varying vec2 vShadowUv;

            void main() {
                vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                vShadowUv = vec2(
                    0.5 + worldPosition.x / shadowScale,
                    0.5 - worldPosition.z / shadowScale
                );
                gl_Position = projectionMatrix * viewMatrix * worldPosition;
            }
        `,
    });
}

/**
 * Displays the CPU-backed cached shadow on a Neutral floor or pedestal top.
 * World-space UV projection keeps the bake aligned even when the receiver
 * geometry changes from a plane to a cylinder footprint.
 */
export default memo(function CachedShadowPlane({
    fadeEdges,
    model,
    pedestal,
    texture,
}:Props) {
    const material = useMemo(createCachedShadowMaterial, []);
    // The baker uses the same radius-derived square, so this value is both the
    // receiver size and the world-to-shadow-UV conversion scale.
    const shadowScale = model.radius * 4;

    useLayoutEffect(() => {
        material.uniforms.shadowMap.value = texture;
        material.uniforms.fadeEdges.value = fadeEdges ? 1 : 0;
        material.uniforms.shadowScale.value = shadowScale;
    }, [fadeEdges, material, shadowScale, texture]);

    useEffect(() => {
        return () => material.dispose();
    }, [material]);

    return <mesh
        position={[
            0,
            pedestal
                ? pedestal.topY + Math.max(model.radius * 0.001, 0.0001)
                : -model.height / 2,
            0,
        ]}
        receiveShadow
        renderOrder={pedestal ? 3 : 0}
        rotation={[-Math.PI / 2, 0, 0]}
    >
        {pedestal?.shape === 'cylinder'
            ? <circleGeometry args={[pedestal.radius, 96]} />
            : <planeGeometry
                args={pedestal
                    ? [pedestal.width, pedestal.depth]
                    : [shadowScale, shadowScale]}
            />}
        <primitive attach="material" object={material} />
    </mesh>;
});
